import { FluidKeyedObject } from '@lmig/fluid-core';
import { BehaviorSubject } from 'rxjs';

/**
 * Interface for current viewport "media state" (breakpoints &
 * touch device support).
 */
export interface FluidMediaState {
  breakpointSm?: MediaQueryList;
  breakpointMd?: MediaQueryList;
  breakpointLg?: MediaQueryList;
  breakpointXl?: MediaQueryList;
  touchDevice?: MediaQueryList;
}

export enum FluidBreakpoint {
  EXTRA_SMALL = 0,
  SMALL = 480,
  MEDIUM = 768,
  LARGE = 992,
  EXTRA_LARGE = 1200,
}

// ================================================================ //
// -- Responsive / Media Query Utils

class FluidResponsiveUtils {
  /**
   * Key/value pairs for all FLUID CSS breakpoints & touch device support.
   * Used to generate "window.matchMedia" queries / event listeners.
   */
  public mediaQueries: FluidKeyedObject<string> = {
    breakpointSm: `(min-width: ${FluidBreakpoint.SMALL}px)`,
    breakpointMd: `(min-width: ${FluidBreakpoint.MEDIUM}px)`,
    breakpointLg: `(min-width: ${FluidBreakpoint.LARGE}px)`,
    breakpointXl: `(min-width: ${FluidBreakpoint.EXTRA_LARGE}px)`,
    touchDevice: '(hover: none)',
  };

  /**
   * Object containing breakpoint names (keys) & their corresponding
   * MediaQueryList objects (values).
   */
  public mediaState: FluidMediaState = {};

  /**
   * Media State BehaviorSubject that pushes updates to Subscribers
   * each time a change in viewport size matches one of the media
   * queries defined in the "mediaQueries" object. When a Subscriber
   * receives an updated media state, it can determine the current
   * viewport size by accessing the MediaQueryList object associated
   * with the desired breakpoint key(s), then accessing the "matches"
   * property of the MediaQueryList object. The "matches" property will
   * be "true" if the current viewport size matches the media query &
   * will be "false" if it doesn't. See the "watchMedia()" method in
   * Sidebar or Header for an example.
   */
  public mediaState$: BehaviorSubject<FluidMediaState> =
    new BehaviorSubject<FluidMediaState>(this.mediaState);

  /**
   * Initializes "window.matchMedia" queries & event listeners for all
   * FLUID CSS breakpoints, then pushes "media state" updates to all
   * "mediaState$" subscribers when touch device support or active
   * breakpoint changes.
   */
  constructor() {
    for (const query in this.mediaQueries) {
      this.mediaState[query] = window.matchMedia(this.mediaQueries[query]);
      this.mediaState[query].addEventListener('change', () => {
        this.mediaState$.next(this.mediaState);
      });
    }
  }
}

export const ResponsiveUtils = new FluidResponsiveUtils();
